'use client';

// Third Party
import * as React from 'react';

// Utils
import { routes } from '@routes';
import { AnalyticsRef } from '@components/analytics-ref';
import { appendUrlParams } from '@utils/append-url-params';

// UI
import { Link } from '@components/link';
import { Typography } from '@components/typography';
import { RatingStars } from '@components/rating-stars';
import { AccountReviewSummary, AccountReviewSummarySource } from '@gql';
import { Icon, IconNames } from '@components/icon';
import { Button } from '@components/button';
import { ExpandableText } from '@components/expandable-text';

type Props = {
  publicPhoneNumber?: string;
  publicEmail?: string;
  account: {
    id?: string;
    activeListingsCount?: number;
    avatarUrl?: string;
    city?: string;
    title?: string;
    description?: string;
    companyName?: string;
    inBusinessSince?: number;
    stateOrProvinceLongName?: string;
    slug?: string;
    facebookPageUrl?: string;
    instagramUrl?: string;
    youtubeUrl?: string;
    twitterUrl?: string;
    pinterestUrl?: string;
    marketingWebsiteUrl?: string;
    linkedinUrl?: string;
    accountReviewSummaries?: AccountReviewSummary[];
  };
};

export const PropertyManagerBio: React.FC<Props> = ({
  publicEmail,
  account: {
    slug,
    description,
    companyName,
    inBusinessSince,
    activeListingsCount,
    marketingWebsiteUrl,
    facebookPageUrl,
    instagramUrl,
    youtubeUrl,
    twitterUrl,
    pinterestUrl,
    title,
    linkedinUrl,
    accountReviewSummaries,
    avatarUrl,
  },
}) => {
  const googleReview = accountReviewSummaries?.find(
    (summary) => summary.source === AccountReviewSummarySource.GoogleReviews
  );

  const getYearsHosting = (year: number) => {
    const currentYear = new Date().getFullYear();
    const yearsHosting = currentYear - year;
    return yearsHosting === 1 ? '1 year' : `${yearsHosting} years`;
  };

  return (
    <div>
      {/* Main About the Host Section */}
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
        {/* Left Column - Card */}
        <div className="lg:w-1/2">
          <div>
            {/* Host Avatar and Name */}
            <div className="flex flex-row items-center gap-3">
              {avatarUrl && (
                <div>
                  <img
                    src={avatarUrl}
                    alt={companyName || 'Host'}
                    className="h-12 w-auto rounded-full border border-gray-light overflow-hidden"
                  />
                </div>
              )}

              <div className="flex flex-col">
                {/* Company Name Link */}
                <Link
                  to={routes.accounts(slug || '')}
                  className="text-lg !underline font-semibold text-primary-main hover:text-primary-main no-underline"
                >
                  {companyName}
                </Link>
                {/* Properties Count */}
                {activeListingsCount !== undefined &&
                  activeListingsCount > 0 && (
                    <Link
                      to={routes.accounts(slug || '')}
                      className="font-light text-gray-main text-sm"
                    >
                      {activeListingsCount === 1
                        ? `${activeListingsCount} Property`
                        : `${activeListingsCount} Properties`}
                    </Link>
                  )}
              </div>
            </div>
            {/* Rating Section */}
            {googleReview && (
              <div className="flex items-center gap-2 mt-2">
                <Typography
                  component="span"
                  className="text-primary-main text-lg font-bold"
                >
                  {googleReview.rating.toFixed(2)}
                </Typography>
                <div className="flex items-center gap-1">
                  <RatingStars rating={googleReview.rating} />
                </div>
                {googleReview.numberOfReviews &&
                  googleReview.numberOfReviews > 0 && (
                    <>
                      {googleReview.url ? (
                        <AnalyticsRef name="select_google_reviews">
                          <a
                            href={googleReview.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-gray-main underline hover:text-gray-dark"
                          >
                            {googleReview.numberOfReviews} Google Reviews
                          </a>
                        </AnalyticsRef>
                      ) : (
                        <span className="text-sm text-gray-main">
                          {googleReview.numberOfReviews} Google Reviews
                        </span>
                      )}
                    </>
                  )}
              </div>
            )}

            {/* Years Hosting */}
            {inBusinessSince && (
              <div className="mt-3">
                <Typography
                  component="span"
                  variant="body2"
                  className="font-light text-gray-main text-sm"
                >
                  {getYearsHosting(inBusinessSince)} hosting
                </Typography>
              </div>
            )}

            {/* Social Media and Website Links */}
            <div className="flex flex-wrap items-center gap-3 mt-4">
              {facebookPageUrl && (
                <AnalyticsRef name="select_listing_social">
                  <a
                    href={facebookPageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-6 h-6 rounded-full bg-primary-main hover:bg-primary-main flex items-center justify-center text-white transition-colors"
                  >
                    <Icon
                      size="sm"
                      name={IconNames.FACEBOOK}
                      className="text-white"
                    />
                  </a>
                </AnalyticsRef>
              )}
              {instagramUrl && (
                <AnalyticsRef name="select_listing_social">
                  <a
                    href={instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-6 h-6 rounded-full bg-primary-main hover:bg-primary-main flex items-center justify-center text-white transition-colors"
                  >
                    <Icon
                      size="sm"
                      name={IconNames.INSTAGRAM}
                      className="text-white"
                    />
                  </a>
                </AnalyticsRef>
              )}
              {linkedinUrl && (
                <AnalyticsRef name="select_listing_social">
                  <a
                    href={linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-6 h-6 rounded-full bg-primary-main hover:bg-primary-main flex items-center justify-center text-white transition-colors"
                  >
                    <Icon
                      size="sm"
                      name={IconNames.LINKEDIN}
                      className="text-white"
                    />
                  </a>
                </AnalyticsRef>
              )}
              {youtubeUrl && (
                <AnalyticsRef name="select_listing_social">
                  <a
                    href={youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-6 h-6 rounded-full bg-primary-main hover:bg-primary-main flex items-center justify-center text-white transition-colors"
                  >
                    <Icon
                      size="sm"
                      name={IconNames.YOUTUBE}
                      className="text-white"
                    />
                  </a>
                </AnalyticsRef>
              )}
              {twitterUrl && (
                <AnalyticsRef name="select_listing_social">
                  <a
                    href={twitterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-6 h-6 rounded-full bg-primary-main hover:bg-primary-main flex items-center justify-center text-white transition-colors"
                  >
                    <Icon
                      size="sm"
                      name={IconNames.TWITTERX}
                      className="text-white"
                    />
                  </a>
                </AnalyticsRef>
              )}
              {pinterestUrl && (
                <AnalyticsRef name="select_listing_social">
                  <a
                    href={pinterestUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-6 h-6 rounded-full bg-primary-main hover:bg-primary-main flex items-center justify-center text-white transition-colors"
                  >
                    <Icon
                      size="sm"
                      name={IconNames.PINTEREST}
                      className="text-white"
                    />
                  </a>
                </AnalyticsRef>
              )}
              {marketingWebsiteUrl && (
                <AnalyticsRef name="select_account_website">
                  <a
                    href={appendUrlParams(marketingWebsiteUrl, {
                      utm_source: 'Savvy',
                      utm_medium: 'website',
                    })}
                    target="_blank"
                    rel="noreferrer"
                    className={`text-sm text-primary-main underline hover:text-primary-main font-medium ${
                      facebookPageUrl ||
                      instagramUrl ||
                      linkedinUrl ||
                      youtubeUrl ||
                      twitterUrl ||
                      pinterestUrl
                        ? 'ml-1'
                        : ''
                    }`}
                  >
                    View Website
                  </a>
                </AnalyticsRef>
              )}
            </div>
            {publicEmail && (
              <div className="mt-4">
                <AnalyticsRef name="select_listing_email">
                  <Button
                    variant="outlined"
                    size="sm"
                    onClick={() => {
                      window.location.href = `mailto:${publicEmail}?subject=Inquiry from Savvy`;
                    }}
                  >
                    Email host
                  </Button>
                </AnalyticsRef>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Description */}
        <div className="lg:w-1/2">
          {/* Professional Host Info */}
          {title && (
            <Typography
              component="h5"
              variant="body2"
              className="text-primary-main !font-light mb-1 !text-base"
            >
              {title}
            </Typography>
          )}
          <div className={title ? 'mt-2' : ''}>
            <Typography
              component="h5"
              variant="body2"
              className="text-primary-main font-semibold mb-1"
            >
              {companyName}
            </Typography>
            {description && (
              <div className="text-primary-main">
                <ExpandableText
                  text={description}
                  maxLines={5}
                  className="!text-base !font-light"
                  showMoreLabel="Show more"
                  showLessLabel="Show less"
                  buttonColor="primary"
                  buttonVariant="text"
                  buttonClassName="underline -ml-[16.5px] mt-1  text-md"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
